import React, { useState, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import axiosInstance from '../../../services/api/axios';
import { useUser } from '../../../context/UserContext';
import { format, subDays, startOfWeek } from 'date-fns';
import { Typography, Card, CardBody, Collapse } from '@material-tailwind/react';
import { Line, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CustomInput from '../../../components/CustomInput';

Chart.register(...registerables);

function SalesDetails() {
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { userInfo } = useUser();
    const customerId = userInfo.loginIdentifierDetails.customer.customerId;
    const [dateFilters, setDateFilters] = useState({
        startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [chartsOpen, setChartsOpen] = useState(false);

    const toggleChartsOpen = () => setChartsOpen(!chartsOpen);

    useEffect(() => {
        const fetchSalesDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const performanceResponse = await axiosInstance.get(
                    `/customer/sales/performance?customer_id=${customerId}&start_date=${dateFilters.startDate}&end_date=${dateFilters.endDate}`
                );

                const salesDataArray = Array.isArray(performanceResponse) ? performanceResponse : [];

                // Extract loginIds from the performance data
                const salesIds = salesDataArray.map(item => item.entity.loginId);

                const salesParams = new URLSearchParams();
                salesIds.forEach(saleId => salesParams.append('loginIds', saleId.toString()));

                const salesResponse = await axiosInstance.get(`/customer/sales?${salesParams.toString()}`);
                // Handle both paginated and non-paginated responses
                const salesResponseArray = salesResponse?.items || (Array.isArray(salesResponse) ? salesResponse : []);

                const mergedSalesData = salesResponseArray.map(sale => {
                  const salespersonInfo = salesDataArray.find(item => item.entity.loginId === sale.loginId);

                  return {
                      ...sale,
                      salespersonName: salespersonInfo ? salespersonInfo.entity.name : 'N/A',
                  };
                });

                setSalesData(mergedSalesData);
                console.log("Sales Details:", mergedSalesData);
            } catch (e) {
                setError(`Error fetching sales details: ${e.message}`);
                console.error("Error fetching sales details:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchSalesDetails();
    }, [customerId, dateFilters]);

    const filteredSalesData = useMemo(() => {
        if (!searchQuery) return salesData;
        const lowerCaseQuery = searchQuery.toLowerCase();
        return salesData.filter(item =>
            item.salespersonName?.toLowerCase().includes(lowerCaseQuery) ||
            item.propertyData?.project?.projectName?.toLowerCase().includes(lowerCaseQuery) ||
            item.propertyData?.propertyNo?.toLowerCase().includes(lowerCaseQuery) ||
            (item.user?.firstName + ' ' + item.user?.lastName)?.toLowerCase().includes(lowerCaseQuery)
        );
    }, [salesData, searchQuery]);

    const timeSeriesData = useMemo(() => {
        const salesBySalespersonAndWeek = filteredSalesData.reduce((acc, sale) => {
            const salesperson = sale.salespersonName;
            const weekStart = format(startOfWeek(new Date(sale.saleDate)), 'yyyy-MM-dd');

            if (!acc[salesperson]) {
                acc[salesperson] = {};
            }

            if (!acc[salesperson][weekStart]) {
                acc[salesperson][weekStart] = {
                    weekStart: weekStart,
                    totalSales: 0,
                };
            }
            acc[salesperson][weekStart].totalSales += 1;
            return acc;
        }, {});

        const allWeeks = new Set();
        Object.values(salesBySalespersonAndWeek).forEach(salesByWeek => {
            Object.keys(salesByWeek).forEach(weekStart => allWeeks.add(weekStart));
        });
        const sortedWeeks = Array.from(allWeeks).sort();

        const datasets = Object.keys(salesBySalespersonAndWeek).map(salesperson => {
            const data = sortedWeeks.map(weekStart => {
                return salesBySalespersonAndWeek[salesperson][weekStart]?.totalSales || 0;
            });

            return {
                label: salesperson,
                data: data,
                fill: false,
                borderColor: '#' + Math.floor(Math.random() * 16777215).toString(16),
                tension: 0.3,
            };
        });

        return {
            labels: sortedWeeks,
            datasets: datasets,
            options: {
                scales: {
                    x: {
                        type: 'category',
                        title: {
                            display: true,
                            text: 'Week Starting',
                        },
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Number of Sales',
                        },
                    },
                },
            },
        };
    }, [filteredSalesData]);

    const pieChartData = useMemo(() => {
        const salesBySalesperson = filteredSalesData.reduce((acc, sale) => {
            const salesperson = sale.salespersonName;
            acc[salesperson] = (acc[salesperson] || 0) + 1;
            return acc;
        }, {});

        const labels = Object.keys(salesBySalesperson);
        const data = labels.map(salesperson => salesBySalesperson[salesperson]);

        return {
            labels: labels,
            datasets: [{
                data: data,
                backgroundColor: labels.map(() => '#' + Math.floor(Math.random() * 16777215).toString(16)),
                hoverOffset: 4
            }],
        };
    }, [filteredSalesData]);

  const pieChartDataAmount = useMemo(() => {
      const salesBySalesperson = filteredSalesData.reduce((acc, sale) => {
          const salesperson = sale.salespersonName;
          acc[salesperson] = (acc[salesperson] || 0) + sale.basePrice;
          return acc;
      }, {});

      const labels = Object.keys(salesBySalesperson);
      const data = labels.map(salesperson => salesBySalesperson[salesperson]);

      return {
          labels: labels,
          datasets: [{
              data: data,
              backgroundColor: labels.map(() => '#' + Math.floor(Math.random() * 16777215).toString(16)),
              hoverOffset: 4
          }],
      };
  }, [filteredSalesData]);

    const columnDefs = useMemo(() => [
        { headerName: "Salesperson Name", field: "salespersonName", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Project Name", field: "propertyData.project.projectName", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Apartment No", field: "propertyData.propertyNo", valueFormatter: (params) => params.value || 'N/A' },
        {
            headerName: "Customer Name",
            field: "user",
            valueFormatter: (params) => {
                const user = params.value;
                if (!user) return 'N/A';
                return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
            }
        },
        { headerName: "Base Value", field: "basePrice", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Sale Date", field: "saleDate", valueFormatter: (params) => params.value ? format(new Date(params.value), 'yyyy-MM-dd') : 'N/A' },
    ], []);

    const handleDateChange = (e) => {
        e.stopPropagation();
        setDateFilters({
            ...dateFilters,
            [e.target.name]: e.target.value,
        });
    };

    const handleSearchChange = (e) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
    };

    return (
        <div>

            
            {/* Date Range and Search */}
            <div className="my-4 flex items-center justify-between">

              
                <CustomInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{height:'px'}}

                />
                <div className="flex items-center space-x-2">

                    <input
                        type="date"
                        name="startDate"
                        value={dateFilters.startDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm"
                    />

                    <input
                        type="date"
                        name="endDate"
                        value={dateFilters.endDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm"
                    />
                </div>
            </div>
            {/* AgGrid Table */}
            {loading ? (
                <Typography>Loading sales details...</Typography>
            ) : error ? (
                <Typography color="red">{error}</Typography>
            ) : (
                <div className="ag-theme-quartz font-normal my-6" style={{ height: '500px', width: '100%' }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={filteredSalesData}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 150,
                            resizable: true,
                            sortable: true,
                            filter: true,
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default SalesDetails;